// Pure sparkline geometry — extracted from sparkline.ts so the path
// builder and nearest-index logic can be property-tested without the
// d3-selection / .dox import chain that the live hook depends on.
//
// Identical semantics to the originals in sparkline.ts; the live hook
// now imports from here so the two stay in lockstep.

import { extent, bisector } from "d3-array";

export interface SparklineMargin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/**
 * Build an SVG `M..L..` path for the linear-regression line fit through
 * the non-null samples in `points`. The fit is computed in *order index*
 * space so it visually aligns with d3-shape's path generator: the j-th
 * valid sample sits at `margin.left + j * xStep` regardless of where the
 * nulls fall in the input array.
 *
 * Returns "" if fewer than two valid samples — there's no line to fit.
 *
 * Endpoints are clamped to the chart's y-domain so a shape like
 * `[0, 1, 2, 100]` (positive slope with one large outlier) doesn't
 * project a regression below the chart's inner area.
 */
export const regressionPath = (
  points: ReadonlyArray<number | null>,
  w: number,
  h: number,
  margin: SparklineMargin,
): string => {
  const valid = points
    .map((v, i) => ({ v, i }))
    .filter((d): d is { v: number; i: number } => d.v != null && !isNaN(d.v));
  if (valid.length < 2) return "";

  const innerW = w - margin.left - margin.right;
  const innerH = h - margin.top - margin.bottom;
  // d3.extent avoids `Math.min/max(...arr)` which stack-overflows past
  // ~125k args on V8 — sparkline windows can hit that ceiling.
  const [yMin = 0, yMax = 1] = extent(valid, (d) => d.v);
  const yRange = yMax - yMin || 1;

  const xStep = innerW / Math.max(valid.length - 1, 1);
  const xOf = (order: number): number => margin.left + order * xStep;
  const yOf = (val: number): number =>
    margin.top + innerH - ((val - yMin) / yRange) * innerH;

  const n = valid.length;
  const meanJ = (n - 1) / 2;
  const meanY = valid.reduce((a, d) => a + d.v, 0) / n;
  const { num, den } = valid.reduce(
    (acc, d, j) => {
      const dj = j - meanJ;
      return { num: acc.num + dj * (d.v - meanY), den: acc.den + dj * dj };
    },
    { num: 0, den: 0 },
  );
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanJ;

  const clampY = (v: number): number => Math.max(yMin, Math.min(yMax, v));
  const y0 = yOf(clampY(intercept));
  const y1 = yOf(clampY(slope * (n - 1) + intercept));

  return `M${xOf(0)},${y0} L${xOf(n - 1)},${y1}`;
};

/**
 * Index of the order-position closest to mouse x in a chart that lays
 * `validLength` points evenly across [margin.left, margin.left + innerW].
 * O(log n) via d3-array.bisector.center — replaces a linear scan.
 *
 * Returns 0 for empty or singleton inputs (no choice but the first).
 */
export const nearestLineIndex = (
  validLength: number,
  mx: number,
  margin: SparklineMargin,
  innerW: number,
): number => {
  if (validLength === 0) return 0;
  if (validLength === 1) return 0;
  const xStep = innerW / Math.max(validLength - 1, 1);
  const orderArray = Array.from({ length: validLength }, (_, j) => j);
  const orderBisector = bisector<number, number>(
    (j) => margin.left + j * xStep,
  );
  return orderBisector.center(orderArray, mx);
};
